import { useEffect, useState } from 'react'
import { chamar } from './api'
import { AtalhoInicio } from './componentes/AtalhoInicio'
import { Espinha } from './componentes/Espinha'
import { ETAPAS } from './etapas'
import { Aceites } from './telas/Aceites'
import { Acompanhar } from './telas/Acompanhar'
import { Ficha } from './telas/Ficha'
import { CnpjExistente } from './telas/CnpjExistente'
import { Concluido } from './telas/Concluido'
import { CAMPOS_CONTATO, Contato } from './telas/Contato'
import { Documentos } from './telas/Documentos'
import { CAMPOS_EMPRESA, Empresa } from './telas/Empresa'
import { Identificacao } from './telas/Identificacao'
import { Revisao } from './telas/Revisao'
import { Socios } from './telas/Socios'
import type { Catalogo, Pendencia } from './tipos'
import { useRascunho } from './useRascunho'

export function App() {
  const [catalogo, setCatalogo] = useState<Catalogo | null>(null)
  const [erroCatalogo, setErroCatalogo] = useState<string | null>(null)
  /* Acompanhamento é um MODO, não uma rota. A Custom Page e o Pages servem uma
     página só, e um router de história brigaria com o `?r=` do link de
     correção — que é reescrito na abertura. Estado local dá conta de duas
     telas. */
  const [acompanhando, setAcompanhando] = useState(false)
  /* A ficha é tela cheia, e não um bloco dentro do painel: ela é impressa, e
     uma regra de impressão sobre um trecho aninhado obrigaria a esconder toda
     a árvore em volta, uma camada de cada vez. */
  const [naFicha, setNaFicha] = useState(false)
  const r = useRascunho()

  useEffect(() => {
    let vivo = true
    chamar<Catalogo>('catalogo')
      .then((linhas) => {
        if (vivo) setCatalogo(linhas[0] ?? null)
      })
      .catch((e: Error) => {
        if (vivo) setErroCatalogo(e.message)
      })
    return () => {
      vivo = false
    }
  }, [])

  const etapa = ETAPAS.find((e) => e.numero === r.etapa) ?? ETAPAS[0]!
  const ocupado = r.gravacao === 'gravando'

  const mostrarAtalhoInicio =
    !naFicha && !acompanhando && !r.concluido && !r.carregando && r.etapa > 1

  async function avancarDe(numero: number, campos: string[]) {
    if (await r.salvar(numero, campos)) r.irPara(numero + 1)
  }

  return (
    <>
      <header className="topo">
        <div className="topo__interno">
          {catalogo?.logoUrl ? (
            <img
              className="topo__logo"
              src={catalogo.logoUrl}
              alt="Nazária Distribuidora"
              style={{ height: `${catalogo.logoAltura ?? 40}px` }}
            />
          ) : (
            <span className="topo__nome">
              Nazária <span>Distribuidora</span>
            </span>
          )}

          {/* Só aparece quando há de onde voltar. Na própria etapa 1 seria um
              atalho para a tela em que a pessoa já está; na conclusão e na
              ficha, um convite a sair de onde ela precisa ficar. E no
              acompanhamento a saída é o "Voltar" da própria tela. */}
          {mostrarAtalhoInicio ? <AtalhoInicio aoIr={() => r.irPara(1)} /> : null}
        </div>
      </header>

      <main className="pagina">
        {catalogo?.formularioAberto === 0 ? (
          <div className="painel">
            <h1 className="painel__titulo">Pré-cadastro indisponível</h1>
            <p className="painel__ajuda">
              O formulário está fora do ar temporariamente. Tente novamente mais tarde ou fale com
              seu representante.
            </p>
          </div>
        ) : naFicha ? (
          <section className="painel">
            <div className="painel__cabeca semimpressao">
              <h1 className="painel__titulo">Ficha cadastral</h1>
            </div>
            <div className="painel__corpo">
              {/* A logo desce do catálogo, que já está em mãos: a impressão
                  esconde o topo, e sem passá-la a folha assinada sairia sem
                  marca. Nenhuma chamada a mais por causa disso. */}
              <Ficha
                aoVoltar={() => setNaFicha(false)}
                logoUrl={catalogo?.logoUrl}
                logoAltura={catalogo?.logoAltura}
              />
            </div>
          </section>
        ) : acompanhando ? (
          <section className="painel">
            <div className="painel__cabeca">
              <h1 className="painel__titulo">Acompanhar solicitação</h1>
              <p className="painel__ajuda">
                Informe o CNPJ e enviaremos um código para o e-mail cadastrado.
              </p>
            </div>
            <div className="painel__corpo">
              <Acompanhar
                aoVoltar={() => setAcompanhando(false)}
                aoAbrirFicha={() => setNaFicha(true)}
              />
            </div>
          </section>
        ) : r.concluido ? (
          <section className="painel">
            <div className="painel__cabeca">
              <h1 className="painel__titulo">Solicitação enviada</h1>
            </div>
            {/* `!== 0`: se o catálogo não carregou, promete a ficha. É o modo
                atual e o conservador — melhor o cliente esperar um documento
                que chega do que não esperar um que vai chegar. */}
            <Concluido
              numero={r.numero}
              prazoAnalise={r.prazoAnalise}
              email={r.dados.email ?? ''}
              exigirFicha={catalogo?.exigirFichaAssinada !== 0}
              /* A troca de tela vem DEPOIS de o `sair` voltar, não junto: ele
                 apaga os dois cookies, e mandar a pessoa para o acompanhamento
                 antes disso deixaria o `Set-Cookie` de limpeza chegar em cima da
                 sessão que o código de acesso acabou de criar. São milissegundos
                 no caminho feliz, e o `sair` limpa a tela local antes de ir à
                 rede — a espera não aparece. */
              aoSair={async (destino) => {
                await r.sair()
                if (destino === 'acompanhar') setAcompanhando(true)
              }}
            />
          </section>
        ) : (
          <div className="colunas">
            <div>
              <Espinha atual={r.etapa} alcancada={r.alcancada} aoIr={r.irPara} />
              {r.existe ? <SeloGravacao estado={r.gravacao} em={r.atualizadoEm} /> : null}
            </div>

            <section className="painel">
              {r.pendencias.length > 0 ? (
                <Pendencias itens={r.pendencias} catalogo={catalogo} />
              ) : null}

              <div className="painel__cabeca">
                <span className="painel__etapa">
                  Etapa {etapa.numero} de {ETAPAS.length}
                </span>
                <h1 className="painel__titulo">
                  {r.cnpjExistente ? 'Este CNPJ já está conosco' : etapa.titulo}
                </h1>
                <p className="painel__ajuda">
                  {r.cnpjExistente
                    ? 'Encontramos um registro para o CNPJ informado.'
                    : textoDeApoio(etapa.numero)}
                </p>
              </div>

              <div className="painel__corpo" key={`${r.etapa}-${r.cnpjExistente ? 'dup' : 'ok'}`}>
                {r.carregando ? (
                  <Carregando />
                ) : r.cnpjExistente ? (
                  /* Vem do `iniciar`. Só aparece na etapa 1, e é a barreira que
                     substituiu o gate de código no canal público. */
                  <CnpjExistente
                    dados={r.cnpjExistente}
                    cnpj={r.dados.cnpj ?? ''}
                    aoRecomecar={r.recomecar}
                  />
                ) : r.etapa === 1 ? (
                  <Identificacao
                    aoIniciar={r.iniciar}
                    ocupado={ocupado}
                    aoAcompanhar={() => setAcompanhando(true)}
                  />
                ) : r.etapa === 2 ? (
                  <Empresa
                    dados={r.dados}
                    tiposCliente={catalogo?.tiposCliente ?? []}
                    aoAlterar={r.alterar}
                    aoDefinirVarios={r.definirVarios}
                    aoVoltar={() => r.irPara(1)}
                    aoAvancar={() => void avancarDe(2, CAMPOS_EMPRESA)}
                    ocupado={ocupado}
                  />
                ) : r.etapa === 3 ? (
                  <Contato
                    dados={r.dados}
                    aoAlterar={r.alterar}
                    aoVoltar={() => r.irPara(2)}
                    aoAvancar={() => void avancarDe(3, CAMPOS_CONTATO)}
                    ocupado={ocupado}
                  />
                ) : r.etapa === 4 ? (
                  <Socios
                    socios={r.socios}
                    aoMudar={r.setSocios}
                    aoVoltar={() => r.irPara(3)}
                    aoAvancar={() => {
                      void r.salvarSocios(r.socios).then((ok) => ok && r.irPara(5))
                    }}
                    ocupado={ocupado}
                  />
                ) : r.etapa === 5 ? (
                  <Documentos
                    catalogo={catalogo}
                    tipoClienteId={r.dados.tipoClienteId ?? ''}
                    socios={r.socios}
                    enviados={r.documentos}
                    aoEnviar={r.anexar}
                    aoVoltar={() => r.irPara(4)}
                    aoAvancar={() => r.irPara(6)}
                    ocupado={ocupado}
                  />
                ) : r.etapa === 6 ? (
                  <Aceites
                    textos={catalogo?.textosAceite ?? []}
                    aceitos={r.aceites}
                    aoMudar={r.setAceites}
                    aoVoltar={() => r.irPara(5)}
                    aoAvancar={() => {
                      void r.salvarAceites(r.aceites).then((ok) => ok && r.irPara(7))
                    }}
                    ocupado={ocupado}
                  />
                ) : (
                  <Revisao
                    dados={r.dados}
                    tiposCliente={catalogo?.tiposCliente ?? []}
                    socios={r.socios}
                    documentos={r.documentos}
                    aceites={r.aceites}
                    aoIr={r.irPara}
                    aoVoltar={() => r.irPara(6)}
                    aoEnviar={() => void r.enviar()}
                    ocupado={ocupado}
                  />
                )}
              </div>

              {r.erro ? (
                <p className="aviso aviso--erro" role="alert">
                  {r.erro}
                </p>
              ) : null}
              {erroCatalogo ? (
                <p className="aviso aviso--erro" role="alert">
                  {erroCatalogo}
                </p>
              ) : null}
            </section>
          </div>
        )}
      </main>
    </>
  )
}

/** Rótulos dos campos devolvíveis, na linguagem da tela. */
const NOME_DO_CAMPO: Record<string, string> = {
  razaoSocial: 'Razão social',
  nomeFantasia: 'Nome fantasia',
  inscricaoEstadual: 'Inscrição estadual',
  cep: 'CEP',
  endereco: 'Endereço',
  numeroEndereco: 'Número',
  complemento: 'Complemento',
  bairro: 'Bairro',
  cidade: 'Cidade',
  uf: 'Estado',
  telefone: 'Telefone',
  whatsapp: 'WhatsApp',
  email: 'E-mail',
}

/**
 * O que a análise pediu para ajustar.
 *
 * Fica no TOPO do painel, acima do conteúdo da etapa, e aparece em todas as
 * etapas — não só naquela onde está o problema. A pessoa chegou aqui por um
 * link de e-mail sem saber onde mexer; esconder a lista atrás de navegação
 * seria devolver a ela o trabalho de procurar.
 *
 * O motivo escrito pelo analista vem em destaque porque é a única parte que
 * realmente informa. O nome do campo apenas localiza.
 */
function Pendencias({ itens, catalogo }: { itens: Pendencia[]; catalogo: Catalogo | null }) {
  function rotulo(p: Pendencia) {
    if (p.campo) return NOME_DO_CAMPO[p.campo] ?? p.campo
    const doc = catalogo?.tiposDocumento?.find((d) => d.id === p.tipoDocumentoId)
    /* Catálogo ainda carregando, ou documento desativado depois da devolução:
       o motivo sozinho ainda diz o que fazer. */
    return doc?.nome ?? 'Documento'
  }

  return (
    <div className="aviso aviso--correcao" role="status">
      <h2 className="aviso__titulo">
        {itens.length === 1
          ? 'Precisamos de um ajuste antes de continuar'
          : `Precisamos de ${itens.length} ajustes antes de continuar`}
      </h2>
      <ul className="aviso__lista">
        {itens.map((p) => (
          <li key={p.id}>
            <strong>{rotulo(p)}</strong>
            {p.socioId ? ' (sócio)' : null} — {p.motivo}
          </li>
        ))}
      </ul>
      <p className="aviso__nota">
        Corrija os itens acima e envie de novo pela última etapa. Seu número de solicitação continua
        o mesmo.
      </p>
    </div>
  )
}

function textoDeApoio(numero: number) {
  if (numero === 1) return 'Vamos começar pelo CNPJ. Você poderá parar e voltar quando quiser.'
  if (numero === 2) return 'Dados da empresa como constam no contrato social.'
  if (numero === 3) return 'Por onde falamos com você durante a análise.'
  if (numero === 4) return 'Sócios como constam no contrato social.'
  if (numero === 5) return 'Os documentos exigidos para o tipo de cliente que você escolheu.'
  if (numero === 6) return 'Leia e aceite antes de enviar.'
  return 'Confira tudo antes de enviar.'
}

function Carregando() {
  return (
    <div className="grade" aria-busy="true">
      <span className="oculto-visual">Carregando seu preenchimento…</span>
      <div className="esqueleto" />
      <div className="esqueleto" />
      <div className="esqueleto" />
    </div>
  )
}

/**
 * Selo de gravação.
 *
 * Numa máquina lenta, a dúvida "será que salvou?" é o que faz a pessoa
 * desistir de um formulário longo. Por isso o estado fica sempre visível, e o
 * texto diz a HORA — vago ("salvo") tranquiliza menos que exato ("salvo 14:32").
 */
function SeloGravacao({ estado, em }: { estado: string; em: string | null }) {
  const hora = em
    ? new Date(em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : null

  const texto =
    estado === 'gravando'
      ? 'Salvando…'
      : estado === 'erro'
        ? 'Não foi possível salvar'
        : hora
          ? `Salvo às ${hora}`
          : 'Salvo'

  return (
    <p className="gravacao" role="status">
      <span
        className={
          'gravacao__ponto' +
          (estado === 'gravando' ? ' gravacao__ponto--gravando' : '') +
          (estado === 'erro' ? ' gravacao__ponto--erro' : '')
        }
        aria-hidden="true"
      />
      {texto}
      {estado !== 'erro' ? (
        <span className="gravacao__nota">Pode fechar e voltar depois neste computador.</span>
      ) : null}
    </p>
  )
}
